import React, { useState, useEffect } from 'react';

/**
 * Owner Inline Text — click-to-edit wrapper used inside the REAL
 * Client View when the owner opens it with `?edit=1`. When `edit` is
 * false it renders the exact same tag/className/style as the plain
 * client page, so the client experience is byte-identical.
 */
const OwnerInlineText = ({
  edit,
  value,
  onSave,
  multi = false,
  placeholder,
  testId,
  as = 'span',
  className,
  style = {},
  children,
  locked = false,
  onLockedClick,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  const Tag = as;

  if (!edit) {
    return (
      <Tag className={className} style={style} data-testid={testId}>
        {children != null ? children : value}
      </Tag>
    );
  }

  if (editing) {
    const commit = () => {
      setEditing(false);
      if ((draft || '') !== (value || '')) onSave(draft || '');
    };
    const common = {
      autoFocus: true,
      value: draft,
      onBlur: commit,
      'data-testid': testId ? `${testId}-input` : undefined,
      className,
    };
    if (multi) {
      return (
        <textarea
          {...common}
          rows={3}
          onChange={(e) => setDraft(e.target.value.slice(0, 500))}
          onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
          style={{
            width: '100%', maxWidth: '640px', display: 'block', margin: '0 auto',
            padding: '10px 14px', background: 'rgba(255,255,255,0.92)',
            border: '1px dashed var(--kahla-taupe, #a69888)', outline: 'none',
            resize: 'vertical', font: 'inherit', color: 'inherit',
            textAlign: 'inherit', lineHeight: 'inherit',
            ...style,
          }}
        />
      );
    }
    return (
      <input
        {...common}
        type="text"
        maxLength={200}
        onChange={(e) => setDraft(e.target.value.slice(0, 200))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        style={{
          background: 'rgba(255,255,255,0.92)',
          border: '1px dashed var(--kahla-taupe, #a69888)', outline: 'none',
          padding: '2px 10px', font: 'inherit', color: 'inherit',
          textAlign: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
          maxWidth: '100%', width: multi ? '100%' : undefined,
          ...style,
        }}
      />
    );
  }

  const isEmpty = !value;
  return (
    <Tag
      className={className}
      data-testid={testId}
      onClick={() => { if (locked) { onLockedClick?.(); return; } setEditing(true); }}
      style={{
        ...style,
        cursor: locked ? 'not-allowed' : 'pointer',
        outline: '1px dashed transparent',
        outlineOffset: '4px',
        transition: 'outline-color 200ms ease, background-color 200ms ease',
        opacity: isEmpty ? 0.55 : style.opacity,
        fontStyle: isEmpty ? 'italic' : style.fontStyle,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.outlineColor = 'var(--kahla-taupe, #a69888)';
        e.currentTarget.style.backgroundColor = 'rgba(201,169,110,0.07)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.outlineColor = 'transparent';
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {(children != null && !isEmpty) ? children : (value || placeholder || '…')}
    </Tag>
  );
};

export default OwnerInlineText;
